import { type components } from "@njbsoft/types";
import { type components as customerComponents } from "@njbsoft/types/customer";
import { type operations as companyOperations } from "@njbsoft/types/company";
import { type operations as fieldOperations } from "@njbsoft/types/field";

/**
 * Runtime arrays for OpenAPI enum types.
 * Source of truth: tooling/openapi/ specs → generated types in @njbsoft/types.
 * These arrays MUST stay in sync with the generated union types.
 */

export type PermitType = components["schemas"]["PermitType"];

export const PERMIT_TYPES = [
  "npdes",
  "sdwa",
  "pretreatment",
  "fog",
  "stormwater_ms4",
  "stormwater_msgp",
  "cross_connection",
  "state",
  "local",
  "caa",
] as const satisfies readonly PermitType[];

export type LimitType = components["schemas"]["LimitType"];

export const LIMIT_TYPES = [
  "daily_maximum",
  "monthly_average",
  "weekly_average",
  "instantaneous_maximum",
  "daily_minimum",
  "instantaneous_minimum",
  "annual_average",
  "monthly_maximum",
  "7_day_average",
  "30_day_average",
  "geometric_mean",
  "annual_total",
  "report_only",
  "mcl",
  "mcl_acute",
  "mrdl",
  "action_level",
  "treatment_technique",
] as const satisfies readonly LimitType[];

export type TestingCompanyLicenseType = components["schemas"]["TestingCompanyLicenseType"];

export const TESTING_COMPANY_LICENSE_TYPES = [
  "business",
  "fire",
  "plumbing",
  "landscape",
  "other",
] as const satisfies readonly TestingCompanyLicenseType[];

export type TestableAssemblyListingSource = components["schemas"]["TestableAssemblyListingSource"];

export const LISTING_SOURCES = [
  "listed",
  "alt_listed",
  "unlisted",
  "unapproved",
] as const satisfies readonly TestableAssemblyListingSource[];

export type FacilityComplianceStatus = components["schemas"]["FacilityComplianceStatus"];

/** The premise's derived device-compliance status, read over its active
 * installations. Member order mirrors the generated union's declaration
 * order (the render order every facility filter select uses) — NOT the
 * severity order the list endpoint sorts `compliance_status` by. */
export const FACILITY_COMPLIANCE_STATUSES = [
  "no_active_devices",
  "out_of_compliance",
  "pending_review",
  "compliant",
] as const satisfies readonly FacilityComplianceStatus[];

export type ComplianceVerdict = components["schemas"]["ComplianceVerdict"];

/** The per-installation compliance verdict, in PRECEDENCE order — NOT the
 * generated union's declaration order. A surface that must pick the worst
 * verdict of a set (or colour every member of the vocabulary) compares
 * indices here rather than re-declaring the order, mirroring
 * `REQUIREMENT_LEVELS`' precedent. */
export const COMPLIANCE_VERDICTS = [
  "terminal",
  "pending_review",
  "failed",
  "overdue",
  "unverified",
  "out_of_service",
  "compliant",
] as const satisfies readonly ComplianceVerdict[];

export type DeviceCategory = components["schemas"]["DeviceCategory"];

/** The three device categories every installation surface picks between; the
 * union is the named DeviceCategory schema. Member order mirrors the
 * generated union's declaration order (the render order every category
 * select uses). */
export const DEVICE_CATEGORIES = [
  "testable_assembly",
  "air_gap",
  "non_testable_device",
] as const satisfies readonly DeviceCategory[];

export type CatalogAssemblyType = components["schemas"]["CatalogAssemblyType"];

/** USC FCCCHR §A catalog assembly-type codes; member order mirrors the
 * generated union's declaration order (the canonical render order). */
export const CATALOG_ASSEMBLY_TYPES = [
  "AVB",
  "DC",
  "DCDA",
  "DCDA_II",
  "PVB",
  "RP",
  "RPDA",
  "RPDA_II",
  "SVB",
] as const satisfies readonly CatalogAssemblyType[];

export type CatalogOrientation = components["schemas"]["CatalogOrientation"];

/** USC FCCCHR catalog orientation stamps (H = horizontal, V* = vertical
 * flow-path sequences); member order mirrors the generated union's
 * declaration order. */
export const CATALOG_ORIENTATIONS = [
  "H",
  "VD",
  "VU",
  "VUH",
  "VUVD",
  "VDVU",
  "VUVU",
  "VDVD",
  "HVD",
] as const satisfies readonly CatalogOrientation[];

export type CatalogStatus = components["schemas"]["CatalogStatus"];

/** compliance.catalog_status — the USC FCCCHR-aligned 3-value listing
 * vocabulary, member order mirroring the generated union. */
export const CATALOG_STATUSES = [
  "approved",
  "previously_approved",
  "suspended",
] as const satisfies readonly CatalogStatus[];

export type CatalogImportStatus = components["schemas"]["CatalogImportStatus"];

/** compliance.catalog_import_status — the org CSV flow occupies
 * `uploaded → applied | aborted`; `parsed` / `failed` belong to the
 * super-admin xlsx flow carried on the shared column. */
export const CATALOG_IMPORT_STATUSES = [
  "uploaded",
  "parsed",
  "applied",
  "aborted",
  "failed",
] as const satisfies readonly CatalogImportStatus[];

// The four identity-refinement lists below carry DB-enum values whose PATCH
// wire shape is a plain string (the DB enum is the authoritative source), so
// their union types derive FROM the arrays rather than a generated alias.

/** USC FCCCHR §A + SDWA §1417 grandfather provisions. */
export const GRANDFATHER_BASES = [
  "pre_1986_install",
  "listing_rescinded_in_service",
  "foreign_manufacture_documented",
  "unknown_legacy",
] as const;
export type AssemblyGrandfatherBasis = (typeof GRANDFATHER_BASES)[number];

/** AWWA M14 §6 + USC FCCCHR §A unlisted-track field-identification tokens. */
export const ASSEMBLY_TYPE_TOKENS = ["RP", "DC", "PVB", "RPDA", "DCDA", "SVB", "AVB"] as const;
export type AssemblyTypeToken = (typeof ASSEMBLY_TYPE_TOKENS)[number];

/** ASSE 1001 / 1024 / 1011 / 1052 non-testable identity standards. */
export const NON_TESTABLE_SUBTYPES = [
  "avb",
  "hose_vb",
  "rdc",
  "dual_check",
  "fixed_ag_drain",
  "barometric_loop",
] as const;
export type NonTestableSubtype = (typeof NON_TESTABLE_SUBTYPES)[number];

/** AWWA M14 §4 field-observed orientations. */
export const ORIENTATIONS_OBSERVED = ["horizontal", "vertical_up", "vertical_down"] as const;
export type AssemblyOrientationObserved = (typeof ORIENTATIONS_OBSERVED)[number];

export type DegreeOfHazard = components["schemas"]["DegreeOfHazard"];

export const DEGREES_OF_HAZARD = [
  "health_hazard",
  "non_health_hazard",
] as const satisfies readonly DegreeOfHazard[];

export type AirGapReceivingVessel = components["schemas"]["AirGapReceivingVessel"];

export const AIR_GAP_RECEIVING_VESSELS = [
  "drain",
  "tank",
  "fixture",
  "sump",
] as const satisfies readonly AirGapReceivingVessel[];

export type DeviceServiceType = components["schemas"]["DeviceServiceType"];

/** The water-service classes an assembly can sit on; the empty select option
 * a consumer renders means "not recorded". */
export const DEVICE_SERVICE_TYPES = [
  "domestic",
  "fire",
  "irrigation",
  "reclaimed",
  "industrial",
  "other",
] as const satisfies readonly DeviceServiceType[];

export type PointOfProtectionAtDevice = components["schemas"]["PointOfProtectionAtDevice"];

/** AWWA M14 §4.2 protection-topology positions; NOT NULL on the installation
 * row, so a consumer select never renders an empty option. */
export const POINTS_OF_PROTECTION = [
  "containment",
  "isolation",
  "fire_service_dedicated",
  "irrigation_dedicated",
  "combined",
] as const satisfies readonly PointOfProtectionAtDevice[];

export type BackflowConditions = components["schemas"]["BackflowConditions"];

/** AWWA M14 §4.2 hydraulic hazard-source conditions; NOT NULL on the
 * installation row, so a consumer select never renders an empty option. */
export const BACKFLOW_CONDITIONS = [
  "backpressure_only",
  "backsiphonage_only",
  "both",
] as const satisfies readonly BackflowConditions[];

export type AlternateListingBody = components["schemas"]["AlternateListingBody"];

/** Issuing bodies for an `alt_listed` testable assembly per USC FCCCHR §A. */
export const ALTERNATE_LISTING_BODIES = [
  "asse",
  "iapmo",
  "csa",
  "ul",
  "nsf",
  "state_specific",
] as const satisfies readonly AlternateListingBody[];

export type TestKitStatus = components["schemas"]["TestKitStatus"];

export const TEST_KIT_STATUSES = [
  "in_service",
  "out_of_service",
  "retired",
] as const satisfies readonly TestKitStatus[];

export type TestKitOwnerKind = components["schemas"]["TestKitOwnerKind"];

export const TEST_KIT_OWNER_KINDS = [
  "testing_company",
  "utility_org",
  "tester",
] as const satisfies readonly TestKitOwnerKind[];

/** Mirrors the API cap; the server answers 422 `testing_company_licenses_too_many` past it. */
export const MAX_LICENSES = 20;

/**
 * Custom field definition types. field_type is an inline OpenAPI enum on
 * CustomFieldDefinition (no named schema), so the union is derived from the
 * generated property and the array is its single runtime home.
 */
export type CustomFieldType = components["schemas"]["CustomFieldDefinition"]["field_type"];

export const CUSTOM_FIELD_TYPES = [
  "text",
  "number",
  "boolean",
  "date",
  "select",
] as const satisfies readonly CustomFieldType[];

/**
 * Custom field entity targets. entity_type is a plain string on the wire
 * (the server-side allow-list is authoritative and widens as new surfaces
 * adopt custom fields), so the union derives FROM this array — its single
 * runtime home. A new surface joins by appending here; every entity select
 * grows with it and no call site changes.
 */
export const CUSTOM_FIELD_ENTITY_TARGETS = [
  "device_installation",
  "facility",
  "account",
  "org_tester",
  "testing_company",
] as const;
export type CustomFieldEntityTarget = (typeof CUSTOM_FIELD_ENTITY_TARGETS)[number];

/**
 * Customer-facing test-request status vocabulary. This is NOT a DB enum — the
 * server derives it from the request + assignment + submission states (the
 * customer never sees raw row statuses) — but the wire enum on
 * CustomerTestRequest is the closed five-value set, and this array is its
 * single runtime home for status pills and filters.
 */
export type CustomerTestRequestStatus =
  customerComponents["schemas"]["CustomerTestRequest"]["status"];

export const CUSTOMER_TEST_REQUEST_STATUSES = [
  "requested",
  "in_progress",
  "under_review",
  "complete",
  "cancelled",
] as const satisfies readonly CustomerTestRequestStatus[];

export type WorkAssignmentOrigin = components["schemas"]["WorkAssignmentOrigin"];

/**
 * Work-item provenance. Member order mirrors the generated union's declaration
 * order, which is the render order every origin select and pill uses.
 */
export const WORK_ASSIGNMENT_ORIGINS = [
  "cadence",
  "schedule",
  "manual",
  "follow_up",
  "customer_requested",
  "letter_claim",
  "serial_claim",
  "account_claim",
  "change_request",
] as const satisfies readonly WorkAssignmentOrigin[];

export type WorkAssignmentStatus = components["schemas"]["WorkAssignmentStatus"];

/**
 * The work-item lifecycle vocabulary. The test-activity search parser and the
 * assignments panel's drill narrowing both iterate this array, so a subset
 * would silently drop a status from the URL axis.
 */
export const WORK_ASSIGNMENT_STATUSES = [
  "scheduled",
  "assigned",
  "in_progress",
  "submitted",
  "completed",
  "cancelled",
] as const satisfies readonly WorkAssignmentStatus[];

export type MessageThreadChannel = components["schemas"]["MessageThreadChannel"];

/**
 * The two conversation lanes, keyed on the (assignment, channel) unique pair.
 * A surface that narrows to one lane names it from here rather than inlining
 * the string, so a wire-level change to the vocabulary breaks the array's
 * `satisfies` check instead of silently filtering to nothing.
 */
export const MESSAGE_THREAD_CHANNELS = [
  "customer_company",
  "utility_tester",
] as const satisfies readonly MessageThreadChannel[];

export type MessageSenderRole = components["schemas"]["MessageSenderRole"];

/**
 * Who authored a message. Every portal labels these itself — the wire carries
 * the role and a profile id, never a name — so this array is what a label map
 * is proven exhaustive against.
 */
export const MESSAGE_SENDER_ROLES = [
  "customer_member",
  "company_member",
  "tester",
  "utility_staff",
] as const satisfies readonly MessageSenderRole[];

export type FormSubmissionStatus = components["schemas"]["FormSubmissionStatus"];

/**
 * The submission review-FSM vocabulary in FSM order, with `imported` — a
 * historical test the org importer carried over, terminal and outside the
 * review queue — last. Every status filter and badge map is proven exhaustive
 * against this array.
 */
export const FORM_SUBMISSION_STATUSES = [
  "submitted",
  "needs_review",
  "accepted",
  "rejected",
  "superseded",
  "imported",
] as const satisfies readonly FormSubmissionStatus[];

export type FormTargetKind = components["schemas"]["FormTargetKind"];

/**
 * compliance.form_target_kind — what a work assignment (and so its submission)
 * is aimed at. Member order mirrors the generated union's declaration order.
 */
export const FORM_TARGET_KINDS = [
  "device_test",
  "premise_inspection",
  "survey",
  "incident",
] as const satisfies readonly FormTargetKind[];

export type AcceptanceRequirement = components["schemas"]["AcceptanceRequirement"];

/**
 * The seven selectable acceptance requirements an org can name in its
 * review/block settings (compliance.acceptance_requirement). Member order
 * mirrors the generated union's declaration order.
 */
export const ACCEPTANCE_REQUIREMENTS = [
  "tester_certified_at_test",
  "gauge_calibrated_at_test",
  "gauge_registered",
  "calibration_certificate_on_file",
  "company_insurance_current",
  "company_business_license_on_file",
  "report_within_window",
] as const satisfies readonly AcceptanceRequirement[];

export type RequirementId = components["schemas"]["RequirementId"];

/**
 * The closed acceptance-requirement vocabulary: the seven selectable
 * requirements (ACCEPTANCE_REQUIREMENTS), the four structural conditions,
 * and the four always-review ids, held at review whatever the org's settings
 * say — `outcome_fail`, `target_stale` and `performer_org_member` ride every
 * persisted evaluation, `change_request_attached` none. Member order mirrors
 * the generated union's declaration order.
 */
export const REQUIREMENT_IDS = [
  "tester_certified_at_test",
  "gauge_calibrated_at_test",
  "gauge_registered",
  "calibration_certificate_on_file",
  "company_insurance_current",
  "company_business_license_on_file",
  "report_within_window",
  "tester_grant_active",
  "company_edge_active",
  "kit_in_service",
  "assignment_open",
  "outcome_fail",
  "target_stale",
  "change_request_attached",
  "performer_org_member",
] as const satisfies readonly RequirementId[];

export type RequirementLevel = components["schemas"]["RequirementLevel"];

/**
 * How strictly an org enforces one requirement, in the wire's own ranking
 * (off < review < block). Order IS the ranking: a surface that has to pick
 * the strictest level of a set compares indices here rather than
 * re-declaring the order.
 */
export const REQUIREMENT_LEVELS = [
  "off",
  "review",
  "block",
] as const satisfies readonly RequirementLevel[];

export type RequirementStatus = components["schemas"]["RequirementStatus"];

/** Whether a requirement was satisfied when evaluated. */
export const REQUIREMENT_STATUSES = [
  "pass",
  "fail",
  "not_applicable",
] as const satisfies readonly RequirementStatus[];

export type RejectionReasonCode = components["schemas"]["RejectionReasonCode"];

/**
 * Machine-readable reasons a reviewer selects when rejecting a submission
 * (compliance.rejection_reason_code). Member order mirrors the generated
 * union's declaration order.
 */
export const REJECTION_REASON_CODES = [
  "tester_certification_expired",
  "tester_certification_not_recognized",
  "tester_not_authorized",
  "gauge_calibration_expired",
  "gauge_not_registered",
  "device_mismatch",
  "incomplete_readings",
  "missing_evidence",
  "illegible",
  "report_late",
  "repair_retest_chain_incomplete",
  "wrong_premise",
  "duplicate",
  "other",
] as const satisfies readonly RejectionReasonCode[];

export type CredentialActivityScope = components["schemas"]["CredentialActivityScope"];

/**
 * Person-keyed credential scopes per ASSE 5110/5120/5130/5150 + state
 * BAT/CCS certification programs, mirroring the compliance
 * credential_activity_scope enum. Member order mirrors the generated union's
 * declaration order — the render order every scope select uses.
 */
export const CREDENTIAL_ACTIVITY_SCOPES = [
  "backflow_tester",
  "cross_connection_surveyor",
  "repairer",
  "program_administrator",
] as const satisfies readonly CredentialActivityScope[];

export type CompanyEvidenceKind =
  companyOperations["uploadCompanyEvidence"]["requestBody"]["content"]["multipart/form-data"]["kind"];

/**
 * The caller-supplied kinds on a company-arm evidence upload (license /
 * insurance COI / anything else worth filing). The wire home is the inline
 * multipart enum on the company stack's upload operation
 * (`uploadCompanyEvidence` — the only writer evidence documents have; the
 * org stack reads); the credential and calibration arms pin their kinds
 * server-side and offer no select. Member order is the render order every
 * kind Select and badge map uses.
 */
export const COMPANY_EVIDENCE_KINDS = [
  "business_license",
  "insurance_coi",
  "other",
] as const satisfies readonly CompanyEvidenceKind[];

/**
 * compliance.company_access_rejection_reason — why a utility refuses a
 * testing company's access request, split curable (cure and resubmit) from
 * terminal (no self-serve exit). The wire enum is inline on the reject
 * request property (no named union in @njbsoft/types), so the union derives
 * FROM the array per GRANDFATHER_BASES. The class map mirrors the single Go
 * classification home (internal/compliance/companyreview) — a client never
 * re-classifies.
 */
export const COMPANY_ACCESS_REJECTION_REASONS = [
  "insurance_documentation_insufficient",
  "license_expired_or_missing",
  "roster_credentials_incomplete",
  "untrustworthy_do_not_reapply",
] as const satisfies readonly NonNullable<
  components["schemas"]["RejectTestingCompanyRequest"]["rejection_reason"]
>[];
export type CompanyAccessRejectionReason = (typeof COMPANY_ACCESS_REJECTION_REASONS)[number];
export type CompanyAccessRejectionClass = "curable" | "terminal";
export const COMPANY_ACCESS_REJECTION_REASON_CLASS: Record<
  CompanyAccessRejectionReason,
  CompanyAccessRejectionClass
> = {
  insurance_documentation_insufficient: "curable",
  license_expired_or_missing: "curable",
  roster_credentials_incomplete: "curable",
  untrustworthy_do_not_reapply: "terminal",
};

/**
 * compliance.retirement_reason — the prior asset's retirement buckets named
 * on Replace (AWWA M14 §6.5). The wire enum is inline on the request
 * property (no named union in @njbsoft/types), so this entry is a hybrid of
 * the file's two shapes: the `satisfies`-against-a-union arm per
 * LISTING_SOURCES / DEGREES_OF_HAZARD, reached inline through the request
 * property per CUSTOM_FIELD_TYPES, and the union exported from the array
 * per GRANDFATHER_BASES.
 */
export const RETIREMENT_REASONS = [
  "end_of_life",
  "regulatory_recall",
  "manufacturer_recall",
  "lost",
  "other",
] as const satisfies readonly NonNullable<
  components["schemas"]["DeviceInstallationReplaceRequest"]["retirement_reason"]
>[];
export type RetirementReason = (typeof RETIREMENT_REASONS)[number];

export type AccessRequestKind = components["schemas"]["AccessRequestKind"];

/**
 * Which shape an access-request inbox row carries: a testing company with its
 * roster, or an independent tester's single grant. Member order mirrors the
 * generated union's declaration order — the render order the inbox's kind
 * filter uses. The row's own `kind` property is held to this union by the
 * `Equal<>` pin in `enums.test.ts`, so the two spellings cannot drift.
 */
export const ACCESS_REQUEST_KINDS = [
  "company",
  "tester",
] as const satisfies readonly AccessRequestKind[];

export type MeterType = components["schemas"]["MeterType"];

/** compliance.meter_type — the water services a premise meter measures;
 * `fire` and `irrigation` carry distinct cross-connection risk profiles from
 * `domestic` service. Member order mirrors the generated union's declaration
 * order — the render order every meter-type select uses. */
export const METER_TYPES = [
  "domestic",
  "fire",
  "irrigation",
  "process",
] as const satisfies readonly MeterType[];

export type MeterSizeUnit = components["schemas"]["MeterSizeUnit"];

/** Unit for a meter's `size` per the meters_size_unit_check constraint;
 * member order mirrors the generated union's declaration order. */
export const METER_SIZE_UNITS = [
  "inches",
  "millimeters",
] as const satisfies readonly MeterSizeUnit[];

export type ViolationStatus = components["schemas"]["ViolationStatus"];

/** compliance.violation_status — the enforcement case lifecycle. Member order
 * mirrors the generated union's declaration order, the render order of every
 * case-status select. */
export const VIOLATION_STATUSES = [
  "open",
  "in_correction",
  "termination_pending",
  "service_discontinued",
  "resolved",
  "appealed",
  "rescinded",
] as const satisfies readonly ViolationStatus[];

export type ViolationType = components["schemas"]["ViolationType"];

/** compliance.violation_type — the cross-connection condition a case names.
 * Member order mirrors the generated union's declaration order. */
export const VIOLATION_TYPES = [
  "unprotected_cross_connection",
  "inadequate_protection",
  "required_assembly_not_installed",
  "test_delinquent",
  "failed_test_uncorrected",
  "improper_installation",
  "unapproved_assembly",
  "removed_or_bypassed",
  "access_denied",
  "unapproved_auxiliary_supply",
  "tester_credential_invalid",
  "test_report_falsified",
  "program_obligation_unmet",
] as const satisfies readonly ViolationType[];

export type ViolationOrigin = components["schemas"]["ViolationOrigin"];

/** compliance.violation_origin — how a case was opened. Member order mirrors
 * the generated union's declaration order. */
export const VIOLATION_ORIGINS = [
  "manual",
  "promoted_work_assignment",
  "promoted_form_submission",
] as const satisfies readonly ViolationOrigin[];

export type ViolationResponsibleParty = components["schemas"]["ViolationResponsibleParty"];

/** compliance.violation_responsible_party — who must correct the condition.
 * Member order mirrors the generated union's declaration order. */
export const VIOLATION_RESPONSIBLE_PARTIES = [
  "customer",
  "tester",
  "water_system",
  "contractor",
] as const satisfies readonly ViolationResponsibleParty[];

export type ViolationDiscoveryMethod = components["schemas"]["ViolationDiscoveryMethod"];

/** compliance.violation_discovery_method — how the condition came to light.
 * Member order mirrors the generated union's declaration order. */
export const VIOLATION_DISCOVERY_METHODS = [
  "hazard_assessment_survey",
  "test_submission",
  "tester_report",
  "complaint",
  "incident_investigation",
  "state_referral",
  "other",
] as const satisfies readonly ViolationDiscoveryMethod[];

export type EnforcementActionType = components["schemas"]["EnforcementActionType"];

/** compliance.enforcement_type — the four acts of enforcement the system never
 * records itself, each noted on a case by hand. Member order mirrors the
 * generated union's declaration order (the order the note dialog's select
 * offers them in). */
export const ENFORCEMENT_ACTION_TYPES = [
  "fee_assessed",
  "administrative_order",
  "referral_to_state",
  "referral_to_licensing_board",
] as const satisfies readonly EnforcementActionType[];

/** The case drawer's per-item rules a violation proposal is refused under, in
 * the generated union's declaration order. The review's refusal
 * (`ViolationProposalDecisionError`) names them as this union; the submission
 * create answers the same seven as a plain-string `error` naming
 * `violation_proposals[i].<member>`, so a reader recognizes one there only by
 * membership in this list — the web composer's port and the field sync's
 * blocked arm both read it. */
export const VIOLATION_PROPOSAL_REFUSAL_TOKENS = [
  "violation_proposal_device_mismatch",
  "violation_device_required_for_type",
  "violation_hazard_type_not_found",
  "violation_hazard_type_inactive",
  "violation_due_before_discovered",
  "violation_tester_required_for_party",
  "violation_tester_not_found",
] as const satisfies readonly components["schemas"]["ViolationProposalDecisionError"]["error"][];

/**
 * The letterhead styles an org's letters print in. The enum is inline on the
 * branding property (no named schema), so the union derives from the generated
 * property, as CUSTOM_FIELD_TYPES does. Member order is the settings card's
 * render order.
 */
export type LetterheadStyle = components["schemas"]["NoticeBranding"]["letterhead_style"];

export const LETTERHEAD_STYLES = [
  "classic",
  "banner",
] as const satisfies readonly LetterheadStyle[];

/**
 * Where a letter recipient's address came from. The enum is inline on the
 * recipient resolution's optional `address_source` (absent when nothing
 * resolved), so the union derives from that property without its absence; the
 * frozen letter snapshot carries the same members. Each member has a caption
 * in the notices bundle under `recipientAddress.source.<member>`.
 */
export type AddressSource = NonNullable<
  components["schemas"]["NoticeRecipientResolution"]["address_source"]
>;

export const ADDRESS_SOURCES = [
  "mailing",
  "billing",
  "service_address",
  "override",
] as const satisfies readonly AddressSource[];

export type ChangeRequestKind = components["schemas"]["ChangeRequestKind"];

export const CHANGE_REQUEST_KINDS = [
  "new_installation",
  "correction",
  "replacement",
  "removal",
] as const satisfies readonly ChangeRequestKind[];

export type ChangeRequestStatus = components["schemas"]["ChangeRequestStatus"];

/** Where one request stands (compliance.change_request_status). */
export const CHANGE_REQUEST_STATUSES = [
  "pending_review",
  "accepted",
  "rejected",
  "withdrawn",
] as const satisfies readonly ChangeRequestStatus[];

export type ChangeRequestAnchorKind = components["schemas"]["ChangeRequestAnchorKind"];

/** What a set came with — the inbox's "Came with" filter and label. */
export const CHANGE_REQUEST_ANCHOR_KINDS = [
  "submission",
  "assignment",
  "intake",
  "standalone",
] as const satisfies readonly ChangeRequestAnchorKind[];

export type ChangeRequestReviewStatusFilter =
  components["schemas"]["ChangeRequestReviewStatusFilter"];

/** The utility list's status filter; also the inbox route's `cr_status` parse. */
export const CHANGE_REQUEST_REVIEW_STATUS_FILTERS = [
  "working_queue",
  "pending_review",
  "rejected_awaiting_tester",
  "accepted",
  "rejected",
  "withdrawn",
  "all",
] as const satisfies readonly ChangeRequestReviewStatusFilter[];

export type ChangeRequestRejectionReason = components["schemas"]["ChangeRequestRejectionReason"];

export const CHANGE_REQUEST_REJECTION_REASONS = [
  "duplicate_assembly",
  "out_of_service_area",
  "not_tracked",
  "assembly_verified_in_service",
  "evidence_insufficient",
  "wrong_kind",
  "identity_unverified",
  "other",
  "installation_test",
  "returned_with_rejected_test",
] as const satisfies readonly ChangeRequestRejectionReason[];

export type ChangeRequestEvidence = components["schemas"]["ChangeRequestEvidence"];

/** The four evidence rows of the org's change-request evidence settings. */
export const CHANGE_REQUEST_EVIDENCE = [
  "photos",
  "gps",
  "installed_by",
  "plumbing_permit",
] as const satisfies readonly ChangeRequestEvidence[];

export type ChangeRequestEvidenceFailureId =
  components["schemas"]["ChangeRequestEvidenceFailure"]["id"];

/** The evidence a request can fail: the four settings plus a Removal's location photo. */
export const CHANGE_REQUEST_EVIDENCE_FAILURE_IDS = [
  "photos",
  "gps",
  "installed_by",
  "plumbing_permit",
  "removed_location",
] as const satisfies readonly ChangeRequestEvidenceFailureId[];

export type RemovalObservation = components["schemas"]["RemovalObservation"];

export const REMOVAL_OBSERVATIONS = [
  "removed",
  "not_present",
  "service_abandoned",
  "other",
] as const satisfies readonly RemovalObservation[];

export type ChangeRequestRemovalVerificationBasis =
  components["schemas"]["ChangeRequestRemovalVerificationBasis"];

export const CHANGE_REQUEST_REMOVAL_VERIFICATION_BASES = [
  "premise_inspection",
  "utility_site_visit",
  "service_removed_by_utility",
  "tester_report",
] as const satisfies readonly ChangeRequestRemovalVerificationBasis[];

export type DeviceRemovedReason = components["schemas"]["DeviceRemovedReason"];

/**
 * The removed reasons a utility reviewer may record when protection is
 * still required — a subset of DeviceRemovedReason (the wire enum also
 * carries the derived and internal-only reasons the reviewer never picks).
 */
export const CHANGE_REQUEST_REMOVED_REASONS = [
  "customer_request",
  "regulator_directive",
  "failed_no_repair",
  "cause_not_determined",
] as const satisfies readonly DeviceRemovedReason[];

export type ChangeRequestDispositionCheck = components["schemas"]["ChangeRequestDispositionCheck"];

export const CHANGE_REQUEST_DISPOSITION_CHECKS = [
  "protection_below_hazard",
  "replaced_without_approval",
  "alternate_listing",
] as const satisfies readonly ChangeRequestDispositionCheck[];

export type ChangeRequestDispositionChoice =
  components["schemas"]["ChangeRequestDispositionChoice"];

export const CHANGE_REQUEST_DISPOSITION_CHOICES = [
  "open_case",
  "accept_without_case",
  "recognized",
  "not_recognized",
] as const satisfies readonly ChangeRequestDispositionChoice[];

export type ChangeRequestCaseDispositionAction =
  components["schemas"]["ChangeRequestCaseDispositionAction"];

export const CHANGE_REQUEST_CASE_DISPOSITION_ACTIONS = [
  "resolve",
  "carry",
  "rescind",
  "leave_open",
] as const satisfies readonly ChangeRequestCaseDispositionAction[];

export type ChangeRequestMandatoryCaseType =
  components["schemas"]["ChangeRequestMandatoryCaseType"];

export const CHANGE_REQUEST_MANDATORY_CASE_TYPES = [
  "unapproved_assembly",
  "required_assembly_not_installed",
  "removed_or_bypassed",
] as const satisfies readonly ChangeRequestMandatoryCaseType[];

/**
 * The review row's triage flags — boolean members of the row, not a wire
 * enum — in the inbox's badge order.
 */
export const CHANGE_REQUEST_FLAGS = [
  "probable_replacement",
  "unapproved_assembly",
  "only_containment",
  "before_visit",
  "via_letter_code",
  "looks_like_replacement",
] as const satisfies readonly (keyof components["schemas"]["ChangeRequestReviewRow"])[];

export type ChangeRequestFlag = (typeof CHANGE_REQUEST_FLAGS)[number];

export type DeviceOfRecordAnswer = components["schemas"]["DeviceOfRecordAnswer"];

/** The tester's answers to "Does the assembly on site match this record?". */
export const DEVICE_OF_RECORD_ANSWERS = [
  "matches",
  "correction",
  "replacement",
  "removal",
] as const satisfies readonly DeviceOfRecordAnswer[];

export type ChangeRequestTesterStatusFilter = NonNullable<
  NonNullable<fieldOperations["listMyChangeRequestSets"]["parameters"]["query"]>["status"]
>;

/**
 * The tester list's status filter. The enum is inline on the list operation's
 * query (no named schema), so the union derives from that parameter.
 */
export const CHANGE_REQUEST_TESTER_STATUS_FILTERS = [
  "needs_action",
  "pending_review",
  "accepted",
  "rejected",
  "withdrawn",
] as const satisfies readonly ChangeRequestTesterStatusFilter[];

export type BulkEditRecordType = components["schemas"]["BulkEditRecordType"];

/** The kinds of record one bulk edit run can change, in wire order. Each token
 * is also the record type's permission entity, so a record-type choice gates on
 * the token directly. */
export const BULK_EDIT_RECORD_TYPES = [
  "device_installations",
  "facilities",
  "accounts",
  "testers",
] as const satisfies readonly BulkEditRecordType[];

export type BulkEditAction = components["schemas"]["BulkEditAction"];

/** What a run does to its field, in wire order; a field's catalog entry lists
 * the subset it offers, and the action picker renders from that subset. */
export const BULK_EDIT_ACTIONS = [
  "set",
  "clear",
  "replace",
  "add_tag",
  "remove_tag",
] as const satisfies readonly BulkEditAction[];

export type BulkEditReplaceMatch = components["schemas"]["BulkEditReplaceMatch"];

/** How a replace's `find` must match, in wire order: the whole value, or every
 * occurrence inside it. */
export const BULK_EDIT_REPLACE_MATCHES = [
  "whole",
  "anywhere",
] as const satisfies readonly BulkEditReplaceMatch[];

export type BulkEditExistingValues = components["schemas"]["BulkEditExistingValues"];

/** A set's choice for records already holding a different non-blank value, in
 * wire order. */
export const BULK_EDIT_EXISTING_VALUES = [
  "overwrite",
  "keep",
] as const satisfies readonly BulkEditExistingValues[];

export type BulkEditValueType = components["schemas"]["BulkEditValueType"];

/** A field's value shape, in wire order; the change step renders the input for
 * the shape and a set's `value` takes its JSON type. */
export const BULK_EDIT_VALUE_TYPES = [
  "text",
  "number",
  "boolean",
  "date",
  "choice",
  "tags",
] as const satisfies readonly BulkEditValueType[];

export type BulkEditLockReason = components["schemas"]["BulkEditLockReason"];

/** Why the catalog shows a field as not editable in bulk, in wire order; the
 * locked-fields list renders one explanation per reason. */
export const BULK_EDIT_LOCK_REASONS = [
  "regulatory_assessment",
  "lifecycle",
  "scheduling",
  "device_identity",
  "record_identity",
  "relationship",
  "location",
  "not_bulk_editable",
  "assignment_authority",
] as const satisfies readonly BulkEditLockReason[];

export type BulkEditWarningCode = components["schemas"]["BulkEditWarningCode"];

/** The cautions a run can carry, in wire order; the review step renders one
 * warning per code. */
export const BULK_EDIT_WARNING_CODES = [
  "audit_warning_field",
  "tag_in_ladder_scope",
  "facilities_fall_out_of_compliance",
  "due_dates_saved",
  "work_cancelled",
  "test_work_cancelled",
  "passed_dates_due_today",
  "devices_start_overdue",
  "devices_stop_overdue",
  "facilities_start_overdue",
  "facilities_stop_overdue",
  "test_work_redated",
  "letter_cycles_stop",
  "devices_pause",
  "facilities_without_published_template",
  "testers_hold_open_work",
  "tester_revoke_irreversible",
  "revert_cannot_restore",
] as const satisfies readonly BulkEditWarningCode[];

/** The warning codes whose title reads a record count, in the order of
 * `BULK_EDIT_WARNING_CODES`: the twin of the API's `CountedWarningCodes()`. */
export const BULK_EDIT_COUNTED_WARNING_CODES = [
  "facilities_fall_out_of_compliance",
  "due_dates_saved",
  "work_cancelled",
  "test_work_cancelled",
  "passed_dates_due_today",
  "devices_start_overdue",
  "devices_stop_overdue",
  "facilities_start_overdue",
  "facilities_stop_overdue",
  "test_work_redated",
  "letter_cycles_stop",
  "devices_pause",
  "facilities_without_published_template",
  "testers_hold_open_work",
] as const satisfies readonly BulkEditWarningCode[];

export type BulkEditRunStatus = components["schemas"]["BulkEditRunStatus"];

/** A run's lifecycle, in wire order: the status badge, the run list's status
 * filter and the poller's stop rule key on these. */
export const BULK_EDIT_RUN_STATUSES = [
  "draft",
  "applying",
  "applied",
  "failed",
] as const satisfies readonly BulkEditRunStatus[];

export type BulkEditItemOutcome = components["schemas"]["BulkEditItemOutcome"];

/** What a run does to one selected record, in wire order; the items table's
 * tabs and outcome badges render from it. */
export const BULK_EDIT_ITEM_OUTCOMES = [
  "change",
  "no_change",
  "skipped",
] as const satisfies readonly BulkEditItemOutcome[];

export type BulkEditSkipReason = components["schemas"]["BulkEditSkipReason"];

/** Why a selected record is left unchanged, in wire order, `value_not_allowed`
 * included; the skip-reason card, the Skipped tab's reason column and its
 * filter render one label per reason. */
export const BULK_EDIT_SKIP_REASONS = [
  "device_lifecycle_terminal",
  "device_facility_archived",
  "facility_archived",
  "tester_revoked",
  "account_archived",
  "device_testing_paused",
  "next_test_due_date_required",
  "inventory_already_has_active_installation",
  "tester_pending",
  "no_open_inspection",
  "earlier_inspection_open",
  "value_too_long",
  "value_too_short",
  "tag_limit_reached",
  "value_not_allowed",
  "existing_value_kept",
  "changed_since_preview",
  "changed_since_run",
] as const satisfies readonly BulkEditSkipReason[];

export type BulkEditFailureCode = components["schemas"]["BulkEditFailureCode"];

/** Why a run that started applying changed nothing, in wire order; the failure
 * banner renders one explanation per code. */
export const BULK_EDIT_FAILURE_CODES = [
  "lock_timeout",
  "statement_timeout",
  "lock_conflict",
  "records_not_writable",
  "custom_field_deleted",
  "ceiling_exceeded",
  "interrupted",
  "internal_error",
] as const satisfies readonly BulkEditFailureCode[];

export type BulkEditRevertBlockedReason = components["schemas"]["BulkEditRevertBlockedReason"];

/** Why a run is not revertible, in wire order; the revert card renders one
 * explanation per reason. */
export const BULK_EDIT_REVERT_BLOCKED_REASONS = [
  "not_applied",
  "is_revert",
  "custom_field_deleted",
  "nothing_changed",
  "irreversible",
] as const satisfies readonly BulkEditRevertBlockedReason[];

export type BulkEditSelectionSource = components["schemas"]["BulkEditSelectionSource"];

/** Where a request's records come from, in wire order; the flow's selection
 * source and its URL parse key on these. A stored run additionally reads
 * `run` (a revert's selection), which is not a request source. */
export const BULK_EDIT_SELECTION_SOURCES = [
  "filters",
  "saved_report",
  "all",
] as const satisfies readonly BulkEditSelectionSource[];

/** Every `code` a bulk edit 422 carries, in wire order; the inline refusals
 * render one message per code and the guard accepts no other. */
export const BULK_EDIT_UNPROCESSABLE_CODES = [
  "bulk_edit_selection_invalid",
  "bulk_edit_selection_over_ceiling",
  "bulk_edit_devices_over_ceiling",
  "bulk_edit_selection_empty",
  "bulk_edit_report_not_found",
  "bulk_edit_report_not_bulk_editable",
  "bulk_edit_field_unknown",
  "bulk_edit_field_locked",
  "bulk_edit_action_not_allowed",
  "bulk_edit_invalid_value",
  "bulk_edit_value_required",
  "bulk_edit_existing_values_required",
  "bulk_edit_existing_values_not_allowed",
  "bulk_edit_note_required",
  "bulk_edit_revert_nothing_to_change",
  "bulk_edit_reason_required",
  "bulk_edit_reason_not_allowed",
] as const satisfies readonly components["schemas"]["BulkEditUnprocessableError"]["code"][];

/** Every `code` a bulk edit 409 carries, in wire order; the stale-draft and
 * busy-org states render one message per code and the guard accepts no other. */
export const BULK_EDIT_CONFLICT_CODES = [
  "bulk_edit_run_in_progress",
  "bulk_edit_draft_expired",
  "bulk_edit_run_not_draft",
  "bulk_edit_run_kind_mismatch",
  "bulk_edit_run_not_revertible",
  "bulk_edit_revert_of_revert",
  "bulk_edit_run_already_reverted",
  "bulk_edit_custom_field_deleted",
  "bulk_edit_run_irreversible",
] as const satisfies readonly components["schemas"]["BulkEditConflictError"]["code"][];

/** Every `code` a bulk edit 403 can carry, in wire order. The route gate's own
 * refusal carries none, so the wire member is optional; the record type's read
 * grant is checked ahead of its update grant. */
export const BULK_EDIT_FORBIDDEN_CODES = [
  "bulk_edit_target_read_not_granted",
  "bulk_edit_target_update_not_granted",
  "bulk_edit_revert_out_of_scope",
  "bulk_edit_field_authority_not_granted",
] as const satisfies readonly NonNullable<
  components["schemas"]["BulkEditForbiddenError"]["code"]
>[];

/** What a notice ladder escalates, in the order the builder's trigger select
 * offers them. */
export const NOTICE_LADDER_TRIGGERS = [
  "device_test_due",
  "premise_inspection_due",
  "device_test_result",
  "premise_inspection_result",
  "case_open",
] as const satisfies readonly components["schemas"]["NoticeLadderTrigger"][];

/** A test result's outcome, in the `FormSubmissionOutcome` enum's declared
 * order. A surface that offers them in another order — the result ladder's
 * Result toggle puts the failed test first — orders them where it offers them. */
export const FORM_SUBMISSION_OUTCOMES = [
  "pass",
  "fail",
] as const satisfies readonly components["schemas"]["FormSubmissionOutcome"][];

/** Which inspections an inspection-result ladder letters by the cases they
 * carry, in the order the builder's case-filter select offers them. */
export const NOTICE_LADDER_INSPECTION_CASE_FILTERS = [
  "any",
  "with_cases",
  "without_cases",
] as const satisfies readonly components["schemas"]["NoticeLadderInspectionCaseFilter"][];
